import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent } from 'react'
import { Icon } from './Icon'

// ── Constants ──────────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Monday-first week (Malaysia default). Index 0 = Mon, 6 = Sun. */
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

// ── Date math helpers (mirrors CalendarRangePicker) ────────────────────────────

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate()
}

/** Convert JS Date.getDay() (0=Sun…6=Sat) to Monday-first index (0=Mon…6=Sun). */
function dowMondayFirst(jsDow: number): number {
  return (jsDow + 6) % 7
}

function addDays(iso: string, delta: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + delta)
  return d.toISOString().slice(0, 10)
}

function parseInitialMonth(iso: string | undefined): { y: number; m: number } {
  if (iso) {
    const y = Number(iso.slice(0, 4))
    const m = Number(iso.slice(5, 7)) - 1
    if (!Number.isNaN(y) && !Number.isNaN(m)) return { y, m }
  }
  const today = new Date()
  return { y: today.getFullYear(), m: today.getMonth() }
}

// ── Props ──────────────────────────────────────────────────────────────────────

export interface CalendarMultiPickerProps {
  /** Currently selected dates (ISO YYYY-MM-DD). */
  value: ReadonlySet<string>
  /** Fired when a date is toggled. The full new set is passed. */
  onChange: (next: Set<string>) => void
  /** Optional earliest allowed date (ISO). Dates before this are disabled. */
  min?: string
  /** Optional latest allowed date (ISO). Dates after this are disabled. */
  max?: string
  /** Public holidays to mark with a dot + name label. */
  holidays?: ReadonlyArray<{ date: string; name: string }>
  /** Initial month to display. Default: current month. Accepts ISO YYYY-MM. */
  initialMonth?: string
  'data-testid'?: string
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * Inline multi-select calendar for the Replacement-grant form.
 *
 * HR clicks individual days to add or remove them from the grant. No range
 * fill, no blackout ribbons, no people-out badges — just selected vs not.
 *
 * Keyboard: Arrow keys move focus, PageUp/PageDown change month, Enter/Space
 * toggle the focused day, Escape blurs the grid.
 *
 * Monday-first week (Malaysia default). 44px min cell height for touch.
 */
export function CalendarMultiPicker({
  value,
  onChange,
  min,
  max,
  holidays,
  initialMonth,
  'data-testid': testId,
}: Readonly<CalendarMultiPickerProps>): JSX.Element {
  const uid = useId()
  const gridId = `cmp-grid-${uid.replace(/:/g, '')}`

  const holidayMap = new Map((holidays ?? []).map((h) => [h.date, h.name]))

  const init = parseInitialMonth(initialMonth)
  const [viewYear, setViewYear] = useState(init.y)
  const [viewMonth, setViewMonth] = useState(init.m)
  const [focusedDay, setFocusedDay] = useState<string | null>(null)

  const todayIso = isoToday()

  // ── Grid cells ─────────────────────────────────────────────────────────────

  const totalDays = daysInMonth(viewYear, viewMonth)
  const firstDowMonday = dowMondayFirst(new Date(viewYear, viewMonth, 1).getDay())
  const cells: Array<string | null> = []
  for (let i = 0; i < firstDowMonday; i++) cells.push(null)
  for (let d = 1; d <= totalDays; d++) cells.push(isoDate(viewYear, viewMonth, d))
  const tail = (7 - (cells.length % 7)) % 7
  for (let i = 0; i < tail; i++) cells.push(null)

  const dayIsos = cells.filter((c): c is string => c !== null)

  // ── Navigation ─────────────────────────────────────────────────────────────

  function prevMonth(): void {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11) }
    else setViewMonth((m) => m - 1)
  }

  function nextMonth(): void {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0) }
    else setViewMonth((m) => m + 1)
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  function toggleDay(iso: string): void {
    if (min && iso < min) return
    if (max && iso > max) return
    const next = new Set(value)
    if (next.has(iso)) next.delete(iso)
    else next.add(iso)
    onChange(next)
  }

  // ── Keyboard navigation (ARIA grid) ────────────────────────────────────────

  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const registerBtn = useCallback((iso: string, el: HTMLButtonElement | null): void => {
    if (el) btnRefs.current.set(iso, el)
    else btnRefs.current.delete(iso)
  }, [])

  useEffect(() => {
    if (!focusedDay) return
    const el = btnRefs.current.get(focusedDay)
    if (el) el.focus()
  }, [focusedDay])

  function handleGridKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (!focusedDay) return
    let next: string | null = null

    switch (e.key) {
      case 'ArrowLeft':  next = addDays(focusedDay, -1); break
      case 'ArrowRight': next = addDays(focusedDay, 1); break
      case 'ArrowUp':    next = addDays(focusedDay, -7); break
      case 'ArrowDown':  next = addDays(focusedDay, 7); break
      case 'PageUp':   e.preventDefault(); prevMonth(); return
      case 'PageDown': e.preventDefault(); nextMonth(); return
      case 'Escape':
        e.preventDefault()
        setFocusedDay(null)
        return
      case 'Enter':
      case ' ':
        e.preventDefault()
        toggleDay(focusedDay)
        return
      default:
        return
    }

    if (next) {
      e.preventDefault()
      const nextYear = Number(next.slice(0, 4))
      const nextMonthNum = Number(next.slice(5, 7)) - 1
      if (nextYear !== viewYear || nextMonthNum !== viewMonth) {
        setViewYear(nextYear)
        setViewMonth(nextMonthNum)
      }
      setFocusedDay(next)
    }
  }

  const gridCols = 'repeat(7, minmax(0, 1fr))'
  const fallbackTabStop = dayIsos.includes(todayIso) ? todayIso : (dayIsos[0] ?? null)

  return (
    <div
      data-testid={testId}
      style={{ userSelect: 'none', width: '100%' }}
    >
      {/* 44px touch targets on mobile */}
      <style>{`
        @media (hover: none), (max-width: 719px) {
          #${gridId} button {
            width: 100% !important;
            min-height: 44px !important;
          }
        }
      `}</style>

      {/* Month navigation — centered group: ‹ Month Year › */}
      <div style={navRowStyle}>
        <button
          type="button"
          onClick={prevMonth}
          aria-label="Previous month"
          style={NAV_BTN}
        >
          <Icon name="chevron_left" size={18} />
        </button>
        <span
          data-testid="multi-picker-month-label"
          style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', minWidth: 140, textAlign: 'center' }}
        >
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          aria-label="Next month"
          style={NAV_BTN}
        >
          <Icon name="chevron_right" size={18} />
        </button>
      </div>

      {/* Day-of-week header */}
      <div
        role="row"
        style={{ display: 'grid', gridTemplateColumns: gridCols, marginBottom: 4 }}
      >
        {DOW.map((d) => (
          <div
            key={d}
            role="columnheader"
            aria-label={d}
            style={dowHeaderStyle}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div
        id={gridId}
        role="grid"
        aria-label={`${MONTHS[viewMonth]} ${viewYear}`}
        aria-multiselectable="true"
        onKeyDown={handleGridKeyDown}
        style={{ display: 'grid', gridTemplateColumns: gridCols }}
      >
        {cells.map((iso, i) => {
          if (!iso) {
            return (
              <div
                key={`gap-${i}`}
                role="gridcell"
                aria-hidden="true"
                style={{ height: 48 }}
              />
            )
          }

          const disabled = Boolean((min && iso < min) || (max && iso > max))
          const selected = value.has(iso)
          const isToday = iso === todayIso
          const holidayName = holidayMap.get(iso)
          const isHoliday = holidayMap.has(iso)

          const isTabStop = iso === (focusedDay ?? fallbackTabStop)
          const isVisuallyFocused = focusedDay !== null && iso === focusedDay

          const dayNum = Number(iso.slice(8))

          return (
            <div
              key={iso}
              role="gridcell"
              aria-selected={selected}
              style={cellWrapStyle}
            >
              <button
                type="button"
                ref={(el) => registerBtn(iso, el)}
                disabled={disabled}
                onClick={() => toggleDay(iso)}
                onFocus={() => setFocusedDay(iso)}
                aria-label={iso}
                aria-pressed={selected}
                tabIndex={isTabStop ? 0 : -1}
                style={dayBtnStyle({ selected, disabled, isToday, isVisuallyFocused })}
              >
                {dayNum}
                {isHoliday ? (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      bottom: 3,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: selected ? 'var(--surface)' : 'var(--text-3)',
                      pointerEvents: 'none',
                    }}
                  />
                ) : null}
              </button>
              {isHoliday && holidayName ? (
                <div
                  aria-hidden="true"
                  style={holidayLabelStyle}
                  title={holidayName}
                >
                  {abbreviate(holidayName)}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Truncate long holiday names to fit the narrow cell. */
function abbreviate(name: string): string {
  return name.length > 10 ? `${name.slice(0, 9)}…` : name
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const navRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  marginBottom: 10,
}

const dowHeaderStyle: CSSProperties = {
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--text-3)',
  padding: '2px 0',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const cellWrapStyle: CSSProperties = {
  height: 56,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingTop: 4,
}

const holidayLabelStyle: CSSProperties = {
  fontSize: 9,
  // Holidays read as another non-working day (same visual weight as
  // Sat/Sun), so the label uses the muted ink grey rather than the
  // amber warning hue.
  color: 'var(--text-3)',
  lineHeight: 1.1,
  textAlign: 'center',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  paddingInline: 2,
  marginTop: 1,
  pointerEvents: 'none',
}

interface DayBtnStyleOpts {
  selected: boolean
  disabled: boolean
  isToday: boolean
  isVisuallyFocused: boolean
}

function dayBtnStyle({
  selected,
  disabled,
  isToday,
  isVisuallyFocused,
}: DayBtnStyleOpts): CSSProperties {
  return {
    position: 'relative',
    width: 40,
    height: 40,
    margin: '0 auto',
    padding: 0,
    borderRadius: '50%',
    border: isToday && !selected
      ? '1px solid var(--steel-blue)'
      : 'none',
    background: selected ? 'var(--steel-blue)' : 'transparent',
    color: selected
      ? 'var(--surface)'
      : disabled
        ? 'var(--border-strong)'
        : 'var(--text)',
    fontSize: 13,
    fontWeight: selected || isToday ? 700 : 400,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.35 : 1,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 80ms ease-out',
    outline: isVisuallyFocused && !selected
      ? '2px solid var(--steel-blue)'
      : undefined,
    outlineOffset: isVisuallyFocused && !selected ? 2 : undefined,
  }
}

const NAV_BTN: CSSProperties = {
  width: 28,
  height: 28,
  padding: 0,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-2)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
